import { useCallback, useState } from "react";

import { DIALOG_MAX_HEIGHT_RATIO } from "../FormDialog";

// Buffer so sub-pixel rounding never tips the dialog into a scrollbar.
const SAFETY_MARGIN_PX = 8;

/**
 * Returns a `maxHeight` for a resizable multiline field inside a dialog so the user can't grow it
 * past the point where the dialog would need to scroll — at any screen height.
 *
 * Everything around the field (title, other fields, actions) is a fixed pixel height, measured
 * once; only the dialog's ceiling scales with the viewport (DIALOG_MAX_HEIGHT_RATIO). So the cap
 * is `calc(<ratio>vh - <overhead>px)`, and the browser keeps it correct as the window resizes.
 *
 * Attach `setFieldRef` to the field's `inputRef` and apply `maxHeight` to the resizable element.
 */
export function useResizableFieldMaxHeight() {
  const [maxHeight, setMaxHeight] = useState<string>();

  const setFieldRef = useCallback((fieldEl: HTMLElement | null) => {
    const paper = fieldEl?.closest<HTMLElement>(".MuiDialog-paper");
    if (!fieldEl || !paper) {
      return;
    }

    // Height of the whole dialog minus the field itself = everything else, which is fixed.
    const overhead = paper.offsetHeight - fieldEl.offsetHeight + SAFETY_MARGIN_PX;
    setMaxHeight(`calc(${DIALOG_MAX_HEIGHT_RATIO * 100}vh - ${overhead}px)`);
  }, []);

  return { setFieldRef, maxHeight };
}
